import { useEffect, useRef, useState } from "react";
import { Bot, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/kit";
import { BACKEND, api } from "@/lib/api";
import { useBiz } from "./shared";

const SUGG = ["Por que meu lucro caiu?", "Quantos clientes preciso para não ter prejuízo?", "Onde posso reduzir custos?", "Posso contratar mais funcionários?",
  "Minha empresa tem condições de expandir?", "Quanto preciso faturar para alcançar minha meta?", "Como melhorar meu fluxo de caixa?", "Quais indicadores pioraram neste mês?"];

export function Consultant() {
  const { cid, month } = useBiz();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { api.get(`/biz/companies/${cid}/chat`).then((r) => setMsgs(r.data)); }, [cid]);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);
  const send = async (q) => {
    const message = (q || text).trim();
    if (!message || busy) { return; }
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { id: `u${Date.now()}`, role: "user", content: message }, { id: "streaming", role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${BACKEND}/api/biz/companies/${cid}/chat/stream`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("pp_token") || ""}` },
        body: JSON.stringify({ message, month }),
      });
      if (!res.ok) { throw new Error((await res.json()).detail || "Erro"); }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) { break; }
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const raw = p.replace(/^data: /, "");
          if (raw === "[DONE]") { continue; }
          const ev = JSON.parse(raw);
          if (ev.delta) { setMsgs((m) => m.map((x) => (x.id === "streaming" ? { ...x, content: x.content + ev.delta } : x))); }
          if (ev.error) { toast.error(ev.error); }
        }
      }
    } catch (e) { toast.error(e.message); }
    setMsgs((m) => m.filter((x) => x.id !== "streaming" || x.content).map((x) => (x.id === "streaming" ? { ...x, id: `a${Date.now()}` } : x)));
    setBusy(false);
  };
  const clear = async () => { await api.delete(`/biz/companies/${cid}/chat`); setMsgs([]); };
  return (
    <Section title="Consultor IA Prospera+" testid="biz-consultant" action={msgs.length > 0 && <Button size="sm" variant="ghost" onClick={clear} data-testid="biz-chat-clear"><Trash2 className="h-4 w-4" /></Button>}>
      <p className="mb-4 text-xs text-slate-400">Responde com base nos números da empresa selecionada em {month}, citando o período e os indicadores usados.</p>
      <div className="max-h-[55vh] min-h-[200px] space-y-3 overflow-y-auto pr-1" data-testid="biz-chat-messages">
        {!msgs.length && <div className="flex flex-wrap gap-2">{SUGG.map((s) => <button key={s} onClick={() => send(s)} className="rounded-full border border-[#1E3A6E] px-3 py-1.5 text-xs text-slate-300 hover:border-[#D4AF37]" data-testid="biz-chat-suggestion">{s}</button>)}</div>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && <Bot className="mt-1 h-5 w-5 shrink-0 text-[#D4AF37]" />}
            <div className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-4 py-2 text-sm ${m.role === "user" ? "bg-[#D4AF37] text-[#0B1A33]" : "bg-[#0B1A33] text-slate-200"}`} data-testid={`biz-chat-${m.role}`}>{m.content || "..."}</div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form className="mt-4 flex gap-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Pergunte sobre sua empresa..." disabled={busy} data-testid="biz-chat-input"
          className="h-11 flex-1 rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-3 text-sm text-white" />
        <Button type="submit" disabled={busy || !text.trim()} data-testid="biz-chat-send"><Send className="h-4 w-4" /></Button>
      </form>
    </Section>
  );
}
